import practicalTestService from 'ApiService/Teacher/practicalTestService';
import testService from 'ApiService/Teacher/TestService';
import Card from 'components/card';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import SelectClass from 'pages/Teacher/Attendance/Report/ReportAttendance/AttendancesReportClass';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AuthContext } from 'contexts/AuthContext';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

const Index = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [classData, setClassData] = useState({ courseId: '', batchId: '' });
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [courses, setCourses] = useState([]);
  const [disable, setDisable] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [outOf, setOutOf] = useState('');
  const [marksMap, setMarksMap] = useState({});
  const [savingMarks, setSavingMarks] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [testToEdit, setTestToEdit] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    contentType: 'typed',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  });

  const { state } = useContext(AuthContext);

  const steps = [
    { stepNo: 1, name: 'Class Info' },
    { stepNo: 2, name: 'Practical Tests' },
  ];

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (currentStep === 2 && classData.batchId) {
      fetchTests();
    }
  }, [currentStep, classData.batchId]);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await practicalTestService.getAllPracticalTests(classData.batchId);
      if (response.data.success) {
        setTests(response.data.data);
      } else {
        setTests([]);
      }
    } catch (error) {
      setTests([]);
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch practical tests.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!testToDelete) return;
    try {
      const response = await practicalTestService.deletePracticalTest(testToDelete);
      if (response.data.success) {
        toast.success('Practical test deleted.');
        setTests(tests.filter((t) => t._id !== testToDelete));
        setDeleteModal(false);
        setTestToDelete(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed.');
    }
  };

  const [totalStudents, setTotalStudents] = useState(0);

  const handleViewSubmissions = async (test) => {
    setSelectedTest(test);
    setShowSubmissions(true);
    try {
      const response = await practicalTestService.getSubmissions(test._id);
      if (response.data.success) {
        setSubmissions(response.data.data);
        setTotalStudents(response.data.totalStudents || 0);
        setOutOf(
          response.data.outOf !== null && response.data.outOf !== undefined
            ? String(response.data.outOf)
            : ''
        );
        const map = {};
        (response.data.data || []).forEach((s) => {
          map[s._id] = s.marks !== null && s.marks !== undefined ? String(s.marks) : '';
        });
        setMarksMap(map);
        setEditing(response.data.outOf === null || response.data.outOf === undefined);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch submissions.');
      }
      setSubmissions([]);
    }
  };

  const handlePermissionAction = async (submissionId, action) => {
    try {
      const response = await practicalTestService.handleDriveLinkPermission(submissionId, action);
      if (response.data.success) {
        toast.success(`Permission ${action} successfully.`);
        setSubmissions(submissions.map((s) =>
          s._id === submissionId
            ? { ...s, driveLinkPermissionStatus: action, driveLinkPermissionResponseAt: new Date() }
            : s
        ));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed.');
    }
  };

  const handleSaveMarks = async () => {
    if (outOf === '' || isNaN(parseFloat(outOf)) || parseFloat(outOf) < 0) {
      toast.error('Please enter a valid Out Of value.');
      return;
    }
    const maxMarks = parseFloat(outOf);
    let clamped = false;
    const marksData = submissions.map((s) => {
      const raw = marksMap[s._id];
      let val = raw === '' || raw === undefined || raw === null ? null : parseFloat(raw);
      if (val !== null && !isNaN(val)) {
        if (val < 0) val = 0;
        if (val > maxMarks) {
          val = maxMarks;
          clamped = true;
        }
      }
      return { submissionId: s._id, marks: val };
    });
    if (clamped) {
      toast(`Some marks exceeded the new Out Of value and were capped to ${maxMarks}.`);
    }
    setSavingMarks(true);
    try {
      const response = await practicalTestService.gradeSubmissions(selectedTest._id, maxMarks, marksData);
      if (response.data.success) {
        toast.success(response.data.message || 'Marks saved successfully.');
        const savedMap = {};
        (response.data.data.submissions || []).forEach((s) => { savedMap[s._id] = s; });
        setSubmissions(submissions.map((s) => (savedMap[s._id] ? { ...s, ...savedMap[s._id] } : s)));
        const map = {};
        (response.data.data.submissions || []).forEach((s) => {
          map[s._id] = s.marks !== null && s.marks !== undefined ? String(s.marks) : '';
        });
        setMarksMap(map);
        setSelectedTest((prev) => ({ ...prev, outOf: response.data.data.outOf }));
        setEditing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save marks.');
    } finally {
      setSavingMarks(false);
    }
  };

  const handleNext = () => {
    if (!disable) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (showSubmissions) {
      setShowSubmissions(false);
      setSelectedTest(null);
      return;
    }
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const formatDateTime = (dateStr, timeStr) => {
    const d = new Date(dateStr);
    return `${d.toLocaleDateString()} ${timeStr}`;
  };

  const toInputDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const openEditModal = (test) => {
    setTestToEdit(test);
    setEditForm({
      title: test.title || '',
      description: test.description || '',
      contentType: test.contentType || 'typed',
      startDate: toInputDate(test.startDate),
      startTime: test.startTime || '',
      endDate: toInputDate(test.endDate),
      endTime: test.endTime || '',
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditModalOpen(false);
    setTestToEdit(null);
  };

  const handleSaveEdit = async () => {
    if (!testToEdit) return;
    const { title, startDate, startTime, endDate, endTime } = editForm;
    if (!title || !startDate || !startTime || !endDate || !endTime) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (endDate < startDate || (endDate === startDate && endTime <= startTime)) {
      toast.error('End date/time must be after start date/time.');
      return;
    }
    setSavingEdit(true);
    try {
      const formData = new FormData();
      formData.append('_id', testToEdit._id);
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      formData.append('contentType', editForm.contentType);
      formData.append('startDate', editForm.startDate);
      formData.append('startTime', editForm.startTime);
      formData.append('endDate', editForm.endDate);
      formData.append('endTime', editForm.endTime);
      const response = await practicalTestService.updatePracticalTest(formData);
      if (response.data.success) {
        toast.success('Practical test updated successfully.');
        setTests(tests.map((t) => (t._id === testToEdit._id ? { ...t, ...response.data.data } : t)));
        setSelectedTest((prev) => (prev?._id === testToEdit._id ? { ...prev, ...response.data.data } : prev));
        setEditModalOpen(false);
        setTestToEdit(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update practical test.');
    } finally {
      setSavingEdit(false);
    }
  };

  const getFileUrls = (test) => {
    const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api/v1';
    const paths = Array.isArray(test.fileFilePath) ? test.fileFilePath : (test.fileFilePath ? [test.fileFilePath] : []);
    const names = Array.isArray(test.fileFileName) ? test.fileFileName : (test.fileFileName ? [test.fileFileName] : []);
    return paths.map((p, i) => ({
      url: `${base}/${p.replace(/\\/g, '/')}`,
      name: names[i] || p.split('/').pop(),
    }));
  };

  const handleDownload = async (url, name) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download file.');
    }
  };

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className={`w-md:2/3 mx-auto h-full ${currentStep === 1 ? 'w-5/6' : 'w-full'} md:px-3 3xl:w-7/12`}>
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] h-full mx-auto pb-3">
          <div className="w-full rounded-[20px]">
            <UseContextProvider>
              {currentStep === 1 && (
                <SelectClass
                  setLoading={setLoading}
                  setDisable={setDisable}
                  setClassData={setClassData}
                  classData={classData}
                  courses={courses}
                  loadingCourses={loadingCourses}
                />
              )}
              {currentStep === 2 && !showSubmissions && (
                <div className="p-6">
                  <h3 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                    Practical Tests
                  </h3>
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : tests.length === 0 ? (
                    <p className="text-gray-500">No practical tests found for this batch.</p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {tests.map((test) => (
                        <div
                          key={test._id}
                          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-600 dark:bg-navy-800"
                        >
                          <div className="flex items-start justify-between">
                            <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
                              {test.title}
                            </h4>
                            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                              Batch #{test.Batch?.batchNumber || 'N/A'}
                            </span>
                          </div>
                          <div className="mt-2 space-y-0.5 text-xs text-gray-500">
                            <p>
                              <span className="font-medium">Structure:</span>{' '}
                              {test.Batch?.batchStructure?.name || 'N/A'}
                            </p>
                            <p>
                              <span className="font-medium">Course(s):</span>{' '}
                              {test.Batch?.batchStructure?.courses?.map(c => c.courseName).join(', ') || 'N/A'}
                            </p>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Start: {formatDateTime(test.startDate, test.startTime)}
                          </p>
                          <p className="text-sm text-gray-500">
                            End: {formatDateTime(test.endDate, test.endTime)}
                          </p>
                          {test.contentType === 'typed' && test.description && (
                            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                              {test.description}
                            </p>
                          )}
                          {test.contentType !== 'typed' && test.fileFileName && (
                            <div className="mt-2 space-y-1">
                              {getFileUrls(test).map((f, i) => {
                                const ext = f.name.split('.').pop().toLowerCase();
                                const isDownloadOnly = ext === 'csv' || ext === 'xlsx' || ext === 'pkt';
                                return (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="text-xs text-gray-400">{f.name}</span>
                                    {isDownloadOnly ? (
                                      <button
                                        onClick={() => handleDownload(f.url, f.name)}
                                        className="cursor-pointer text-xs text-brand-600 underline"
                                      >
                                        Download
                                      </button>
                                    ) : (
                                      <a
                                        href={f.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-brand-600 underline"
                                      >
                                        View
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500">
                              Submissions: {test.submissionCount || 0}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEditModal(test)}
                                className="rounded-lg border border-brand-500 bg-white px-4 py-1.5 text-xs font-medium text-brand-500 hover:bg-brand-500 hover:text-white dark:border-brand-400 dark:bg-transparent dark:text-brand-400 dark:hover:bg-brand-400 dark:hover:text-white"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleViewSubmissions(test)}
                                className="rounded-lg bg-brand-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-800"
                              >
                                View Submissions
                              </button>
                              <button
                                onClick={() => {
                                  setTestToDelete(test._id);
                                  setDeleteModal(true);
                                }}
                                className="rounded-lg bg-red-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {currentStep === 2 && showSubmissions && selectedTest && (
                <div className="p-6">
                  <h3 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
                    Submissions: {selectedTest.title}
                  </h3>
                  <p className="mb-1 text-sm text-gray-500">
                    Start: {formatDateTime(selectedTest.startDate, selectedTest.startTime)} | End: {formatDateTime(selectedTest.endDate, selectedTest.endTime)}
                  </p>
                  <p className="mb-4 text-sm font-medium text-gray-600">
                    {submissions.length} / {totalStudents} students submitted
                  </p>
                  <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                    {editing ? (
                      <div className="flex items-end gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-navy-700 dark:text-white">
                            Out Of (Max Marks)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={outOf}
                            onChange={(e) => setOutOf(e.target.value)}
                            placeholder="e.g. 50"
                            className="w-32 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                          />
                        </div>
                        <button
                          onClick={handleSaveMarks}
                          disabled={savingMarks}
                          className="rounded-lg bg-brand-900 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60"
                        >
                          {savingMarks ? 'Saving...' : 'Save Marks'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-navy-700 dark:text-white">
                            Out Of:
                          </span>
                          <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                            {outOf || '—'}
                          </span>
                        </div>
                        <button
                          onClick={() => setEditing(true)}
                          className="rounded-lg bg-brand-900 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                  {submissions.length === 0 ? (
                    <p className="text-gray-500">No submissions yet.</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Student</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Submitted At</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">GitHub Link</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Submission File</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Drive Link</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Drive Permission</th>
                              <th className="px-3 py-2 text-left font-medium text-navy-700 dark:text-white">Marks</th>
                            </tr>
                          </thead>
                        <tbody>
                          {submissions.map((sub) => (
                            <tr key={sub._id} className="border-b border-gray-100 dark:border-navy-700">
                              <td className="px-3 py-2 text-navy-700 dark:text-white">
                                {sub.student?.name || 'Unknown'}
                              </td>
                              <td className="px-3 py-2 text-gray-500">
                                {new Date(sub.submittedAt).toLocaleString()}
                              </td>
                              <td className="px-3 py-2">
                                <a
                                  href={sub.githubLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-600 underline"
                                >
                                  Link
                                </a>
                              </td>
                              <td className="px-3 py-2">
                                {sub.codeFilePath ? (
                                  <a
                                    href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api/v1'}/${sub.codeFilePath.replace(/\\/g, '/')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-brand-600 underline"
                                  >
                                    {sub.codeFileName || 'Download ZIP'}
                                  </a>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {sub.driveLink ? (
                                  <a
                                    href={sub.driveLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-brand-600 underline"
                                  >
                                    Link
                                  </a>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {sub.driveLinkPermissionStatus === 'requested' ? (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handlePermissionAction(sub._id, 'approved')}
                                      className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handlePermissionAction(sub._id, 'denied')}
                                      className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                    >
                                      Deny
                                    </button>
                                  </div>
                                ) : (
                                  <span className={`text-xs font-medium capitalize ${
                                    sub.driveLinkPermissionStatus === 'approved' ? 'text-green-600' :
                                    sub.driveLinkPermissionStatus === 'denied' ? 'text-red-600' :
                                    'text-gray-400'
                                  }`}>
                                    {sub.driveLinkPermissionStatus}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={outOf || undefined}
                                  value={marksMap[sub._id] ?? ''}
                                  onChange={(e) => setMarksMap({ ...marksMap, [sub._id]: e.target.value })}
                                  placeholder="—"
                                  className="w-20 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={handleSaveMarks}
                        disabled={savingMarks}
                        className="rounded-lg bg-brand-900 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60"
                      >
                        {savingMarks ? 'Saving...' : 'Save Marks'}
                      </button>
                    </div>
                    </>
                  )}
                </div>
              )}
            </UseContextProvider>
          </div>
          <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
            <button
              onClick={handleBack}
              className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
            >
              Back
            </button>
            {currentStep < steps.length && (
              <button
                disabled={disable}
                onClick={handleNext}
                className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
              >
                Next
              </button>
            )}
          </div>
        </Card>
      </div>
      <ConfirmationModal
        isOpen={deleteModal}
        onClose={() => { setDeleteModal(false); setTestToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Practical Test"
        message="Are you sure you want to delete this practical test? All submissions will also be deleted."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="warning"
      />
      {editModalOpen && testToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700 md:mx-0">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-base font-medium dark:text-white md:text-lg">
                  Edit Practical Test
                </h2>
                <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {testToEdit.title}
                </p>
              </div>
              <button
                onClick={closeEditModal}
                disabled={savingEdit}
                className="text-xl font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-300 dark:hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  End Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={editForm.endTime}
                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            </div>

            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Assigned students will be notified of the new schedule.
            </p>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={closeEditModal}
                disabled={savingEdit}
                className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-500 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="rounded-full border-2 border-brand-900 bg-brand-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-brand-300 disabled:opacity-70"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
